import { css } from "@emotion/css";
import { useMemo } from "react";

import { Divider, Icon, Text, useColors } from "@certn/cascadia-react";

import { StepType } from "./ActivityStepper";
interface StepItemProps {
  step: StepType;
  index: number;
  isCurrentStep: boolean;
  isLastStep: boolean;
}
interface StepperProps {
  steps: StepType[];
}
export function ProgressStepper(props: StepperProps) {
  const { steps } = props;

  // first incomplete step marks the current step
  const firstIncompleteStep = useMemo(
    () => steps.findIndex((step) => step.status === "INCOMPLETE"),
    [steps]
  );

  return (
    <>
      {steps.map((step, index: number) => (
        <StepItem
          key={index}
          step={step}
          index={index}
          isCurrentStep={firstIncompleteStep === index}
          isLastStep={steps.length - 1 === index}
        />
      ))}
    </>
  );
}

const StepItem = ({
  step,
  index,
  isCurrentStep,
  isLastStep,
}: StepItemProps) => {
  const isComplete = step.status === "COMPLETE";
  const stepLabel = (index + 1).toString();
  const colors = useColors();
  let backgroundColor = "var(--background-selected-weak-hover)";

  if (!isComplete && isLastStep) backgroundColor = "var(--surface-base)";

  if (isCurrentStep)
    backgroundColor = "var(--background-accent-neutral-strong)";

  if (isComplete && isLastStep)
    backgroundColor = "var(--background-accent-green-strong)";

  return (
    <div key={index}>
      <div className={styles.title}>
        <div
          className={css(
            styles.icon,
            { backgroundColor },
            !isComplete &&
              isLastStep && {
                boxShadow: `inset 0 0 0 1px ${colors["border-base"]}`,
              }
          )}
        >
          {isComplete ? (
            <Icon
              name="check"
              type="mini"
              color={isComplete && !isLastStep ? "icon-base" : "icon-inverse"}
            />
          ) : (
            <Text
              color={isCurrentStep ? "text-inverse" : "text-weak"}
              style="global/label-small"
            >
              {stepLabel}
            </Text>
          )}
        </div>
        <Text
          className={css({ marginLeft: "var(--space-3)" })}
          color="text-base"
          style="form/body-primary"
        >
          {step.title}
        </Text>
      </div>

      {step.description && (
        <div className={styles.description}>
          <Divider
            isVertical
            className={css({ visibility: isLastStep ? "hidden" : undefined })}
            borderLeftColor={colors["border-strong"]}
          />

          <Text
            className={css({
              marginLeft: "var(--space-6)",
              visibility: isCurrentStep || isComplete ? undefined : "hidden",
            })}
            color="text-placeholder"
            style="form/body-secondary"
          >
            {step.description}
          </Text>
        </div>
      )}
    </div>
  );
};

const styles = {
  title: css({ display: "flex" }),
  icon: css({
    height: "var(--space-6)",
    width: "var(--space-6)",
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "1px solid red",
  }),
  description: css({
    display: "flex",
    margin: "var(--space-3)",
  }),
};
